import { Controller } from '@hotwired/stimulus'
import { watch, nextTick } from 'vue'
import { SfLog } from '@app/adapters/logger'
import { useDropdown } from '@app/shared/pinia/dropdown'
import getPiniaInstance from '@app/shared/pinia/instance'
import SFetch from '@app/utils/sfetch'
import useI18n from '@app/shared/vue/composables/use-I18n'
import {
  endedRunRef,
  subscribeToRunState,
} from '@app/insights/run-state-channel'
import type { QueryResultsMessage } from '@app/insights/types'

// Mirrors Lists::SfListItemComponent's selected-state classes for the
// `background: "default"` variant used by _segments_list.html.slim
// (sf_list_item_component.rb#selected_classes/#background_classes/
// #hover_classes/#text_color_classes). Duplicated here because "selected"
// can only be resolved client-side — see markSelectedSegment.
const UNSELECTED_ROW_CLASSES = [
  'tw-bg-sf-snow-lighter',
  'hover:tw-bg-sf-slate-subtle-2',
  'tw-text-sf-slate',
  'hover:tw-text-sf-slate-dark-1',
]
const SELECTED_ROW_CLASSES = [
  'tw-bg-sf-primary-subtle',
  'tw-text-sf-slate-dark-2',
]

// The two label widths _segments_list.html.slim picks between: a row showing a
// spinner has less room for its label than one that is not. Mirrored here so a
// row that loses its spinner gets that room back.
const LABEL_WIDTH_BESIDE_SPINNER = 'tw-max-w-[170px]'
const LABEL_WIDTH_ALONE = 'tw-max-w-[200px]'

// Mounted on the recent-queries dropdown menu content, which stays in the DOM
// at all times and is only toggled via v-show inside the sf-ce-dropdown-wrapper
// web component. There's no DOM event to hook into across that boundary, so
// this watches the shared dropdown store directly and focuses the search
// input once this dropdown becomes the open one.
export default class extends Controller {
  static targets = ['searchInput', 'content', 'runSpinner', 'runState']
  static values = { dropdownId: String, quickAccessUrl: String }

  declare readonly searchInputTarget: HTMLInputElement
  declare readonly contentTarget: HTMLElement
  declare readonly runSpinnerTargets: HTMLElement[]
  declare readonly runStateTarget: HTMLElement
  declare readonly hasRunStateTarget: boolean
  declare readonly dropdownIdValue: string
  declare readonly quickAccessUrlValue: string

  private store = useDropdown(getPiniaInstance())
  private stopWatch: (() => void) | null = null
  // Fetched once per page load and reused on every subsequent open — reset
  // naturally on full navigation since the controller reconnects from scratch.
  private loaded = false
  private loading = false
  private stopRunStateListening: (() => void) | null = null
  // Bumped on every connect and disconnect, so a fetch that resolves across
  // either can tell that its DOM and its cleanup are already gone.
  private connectionGeneration = 0
  private inFlightRequest: AbortController | null = null

  connect(): void {
    this.connectionGeneration += 1
    // Reconnecting on the same element keeps the list injected on the first
    // open, spinners and all, but disconnect() gave up its share of the
    // results channel. Take it back, or those spinners turn for good.
    this.subscribeToRunState()
    this.stopWatch = watch(
      () => this.store.currentDropdownId,
      (currentDropdownId) => {
        if (currentDropdownId !== this.dropdownIdValue) return
        void this.loadSegments()
        // The dropdown-wrapper's own v-show update happens in a separate Vue
        // app instance, so wait for it to flush before focusing — otherwise
        // the input may still be display:none and silently fail to focus.
        void nextTick(() => {
          this.resetFilter()
          this.searchInputTarget.focus()
        })
      }
    )
  }

  // Leaves `loaded` false on failure, so the next open retries instead of
  // getting stuck on an error message.
  private async loadSegments(): Promise<void> {
    if (this.loaded || this.loading) return

    const generation = this.connectionGeneration
    this.loading = true
    this.inFlightRequest = new AbortController()
    try {
      const html = await SFetch.html(this.quickAccessUrlValue, {
        signal: this.inFlightRequest.signal,
      })
      // Disconnected while this was in flight: disconnect() has already run its
      // cleanup, so injecting the list here would leak a subscription onto DOM
      // nobody is looking at.
      if (generation !== this.connectionGeneration) return
      if (html === null) return

      this.contentTarget.innerHTML = html
      this.loaded = true
      this.markSelectedSegment()
      this.subscribeToRunState()
      // A keystroke that landed while this fetch was in flight has nothing
      // to filter yet; the freshly-injected list needs the current value
      // re-applied now that it exists, or it shows fully unfiltered.
      if (this.searchInputTarget.value) this.reapplyFilter()
    } catch (err) {
      if (generation !== this.connectionGeneration) return

      SfLog.warn('Error fetching quick access segments', {
        extra: { error: err },
      })
      this.showLoadError()
    } finally {
      this.loading = false
      this.inFlightRequest = null
    }
  }

  // Replaces the spinner _segments.html.slim renders by default. `loaded`
  // stays false (see the comment above), so closing and reopening the
  // dropdown retries — this only makes the failure visible in the meantime.
  private showLoadError(): void {
    const message = document.createElement('div')
    message.className =
      'tw-flex tw-justify-center tw-py-4 tw-text-sf-slate tw-text-sm'
    message.setAttribute('role', 'alert')
    message.textContent = useI18n().t('shared.start_subnav.query_load_error')

    this.contentTarget.replaceChildren(message)
  }

  // The menu content stays mounted (v-show, not v-if) while closed, so a
  // filter typed before closing is still applied when the dropdown reopens.
  // Clear it and re-dispatch keyup — text-filter#filter (bound to this same
  // element) re-shows every item, and the search input's own clear-icon
  // behavior (setup-search-input.ts) only listens for keyup, so an `input`
  // event alone re-filters but leaves the "×" icon visibly stuck on.
  private resetFilter(): void {
    if (!this.searchInputTarget.value) return
    this.searchInputTarget.value = ''
    this.reapplyFilter()
  }

  // Re-runs text-filter#filter (bound to keyup on this same element) against
  // whatever value is currently in the box.
  private reapplyFilter(): void {
    this.searchInputTarget.dispatchEvent(
      new KeyboardEvent('keyup', { bubbles: true })
    )
  }

  // The fetched list is rendered by the quick_access XHR, whose own request
  // path is never the page the user is actually looking at — so
  // current_page?(segment[:href]) inside that partial can never match and
  // every row renders unselected. Resolve "selected" here instead, against
  // the real page's URL, using the same query params each row's href
  // already carries (segment_id / partner_segment_id), rather than the
  // XHR's ambient one.
  private markSelectedSegment(): void {
    const current = new URLSearchParams(window.location.search)
    const currentSegmentId = current.get('segment_id')
    const currentPartnerSegmentId = current.get('partner_segment_id')
    const currentFilters = current.get('filters')
    const isAllClientsPage =
      !currentSegmentId &&
      !currentPartnerSegmentId &&
      (!currentFilters || currentFilters === '[]')

    this.contentTarget
      .querySelectorAll<HTMLAnchorElement>(
        "[data-behavior='segment-item'] a[href]"
      )
      .forEach((link) => {
        const target = new URL(link.href)
        if (target.pathname !== window.location.pathname) return

        const rowSegmentId = target.searchParams.get('segment_id')
        const rowPartnerSegmentId =
          target.searchParams.get('partner_segment_id')
        const isMatch =
          (currentSegmentId && rowSegmentId === currentSegmentId) ||
          (currentPartnerSegmentId &&
            rowPartnerSegmentId === currentPartnerSegmentId) ||
          (isAllClientsPage && !rowSegmentId && !rowPartnerSegmentId)
        if (!isMatch) return

        const row = link.closest<HTMLElement>("[data-behavior='segment-item']")
        row?.classList.remove(...UNSELECTED_ROW_CLASSES)
        row?.classList.add(...SELECTED_ROW_CLASSES)
      })
  }

  // The fetched list carries the channel and the backlog position to listen
  // from, and only when one of its rows has a spinner waiting on a run. Called
  // again on reconnect, so it has to be a no-op while already listening and
  // when every run this list was showing has since ended.
  private subscribeToRunState(): void {
    if (this.stopRunStateListening) return
    if (!this.hasRunStateTarget || this.runSpinnerTargets.length === 0) return

    const channel = this.runStateTarget.dataset.runStateChannel
    const lastId = Number(this.runStateTarget.dataset.runStateLastId)
    if (!channel || !Number.isFinite(lastId)) return

    this.stopRunStateListening = subscribeToRunState(
      channel,
      lastId,
      (message) => this.handleRunStateMessage(message)
    )
  }

  // A run of a query this list shows no spinner for matches nothing below.
  private handleRunStateMessage(message: QueryResultsMessage): void {
    const requestRef = endedRunRef(message)
    if (!requestRef) return

    this.runSpinnerTargets.forEach((spinner) =>
      this.dropRunFromSpinner(spinner, requestRef)
    )

    if (this.runSpinnerTargets.length === 0) this.unsubscribeFromRunState()
  }

  // A query can have several runs going at once, and the row shows one spinner
  // for all of them — it goes when the last one ends, not the first.
  private dropRunFromSpinner(spinner: HTMLElement, requestRef: string): void {
    const refs = (spinner.dataset.requestRefs || '').split(' ').filter(Boolean)
    if (!refs.includes(requestRef)) return

    const remaining = refs.filter((ref) => ref !== requestRef)

    if (remaining.length === 0) {
      this.removeSpinner(spinner)
    } else {
      spinner.dataset.requestRefs = remaining.join(' ')
    }
  }

  private removeSpinner(spinner: HTMLElement): void {
    spinner
      .closest("[data-behavior='segment-item']")
      ?.querySelector("[data-behavior='segment-label']")
      ?.classList.replace(LABEL_WIDTH_BESIDE_SPINNER, LABEL_WIDTH_ALONE)

    spinner.remove()
  }

  private unsubscribeFromRunState(): void {
    this.stopRunStateListening?.()
    this.stopRunStateListening = null
  }

  disconnect(): void {
    this.connectionGeneration += 1
    this.stopWatch?.()
    this.stopWatch = null
    this.inFlightRequest?.abort()
    this.unsubscribeFromRunState()
  }
}
